import React, {
	useState,
	useEffect,
	useMemo,
	useRef,
	useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Table } from "layout/Table/Table";
import { useAssociatedCourseData } from "hooks/RangeDetails";
import { fetch_google_geocode } from "redux/CourseMapSlice";
import { useDispatch } from "react-redux";
import {
	course_details,
	clearCourseListData,
	course_list,
} from "redux/CourseSlice";
import { decryptSecretKey } from "services/utils";
import { debounce } from "lodash";
import Wrapper from "layout/Wrapper/Wrapper";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import HeatMapListSkeleton from "ui/Skeletons/HeatMapListSkeleton";
import useNotiStack from "hooks/useNotistack";
import CourseMapListTableRowItem from "./CourseMapListTableRowItem/CourseMapListTableRowItem";
import TableSkeletonRows from "ui/Skeletons/TableSkeletonRows";

import styles from "styles/pages/HeatMapList.module.scss";

const CourseMapList = () => {
	const [page, setPage] = useState(1);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [hasMoreItems, setHasMoreItems] = useState(true);
	const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
	const [courses, setCourses] = useState([]);
	const containerRef = useRef(null);
	const navigate = useNavigate();
	const location = useLocation();
	const courseData = useAssociatedCourseData();
	const dispatch = useDispatch();
	const { toastError } = useNotiStack();
	const profileDetails = JSON.parse(localStorage.getItem("profile_data"));
	const id_adminuser = localStorage.getItem("id_adminuser");
	const secret_Key = decryptSecretKey();	
	
	useEffect(() => {
		if (courseData?.course_list_data?.courseList?.length > 0) {
			if (page === 1) {
				setCourses(courseData.course_list_data.courseList);
			} else {
				setCourses(prev => [...prev, ...courseData.course_list_data.courseList]);
			}
			
			setHasMoreItems(
				courseData.course_list_data.courseList.length <
					(courseData.course_list_data.totalCourses || 0)
			);
		}
	}, [courseData.course_list_data, page]); 

	useEffect(() => {
		setCourses([]);
		setPage(1);
		setHasMoreItems(true);
	}, [courseData.searchedCourseName]);

	const fetchCourseList = useCallback(
  (page = 1, reset = false) => {
    const trimmed = (courseData.searchedCourseName || "").trim();
    const isNumber = /^\d+$/.test(trimmed);
    const isIdCode12 = /^[A-Za-z0-9]{12}$/.test(trimmed);

    const userPayload = {
      secretKey: secret_Key,
      userName: profileDetails?.username,
    };

    if (!trimmed) return Promise.resolve();

    if (reset) {
      setPage(1);
      dispatch(clearCourseListData());
      setCourses([]);
    } else {
      setPage(page);
    }

    setIsLoadingMore(true);

    // 1) search by integer id_courseInt
    if (isNumber) {
      const idInt = parseInt(trimmed, 10);
      dispatch(clearCourseListData());
      setCourses([]);
      return dispatch(
        course_details({
          body: {
            id_adminuser,
            id_courseInt: idInt,
            stateFormat: 4,
            countryFormat: 4,
            detailLevel: 2,
          },
          user: userPayload,
        })
      )
        .then((resp) => {
          if (resp?.payload?.data?.Status === 1) {
            setCourses([resp.payload.data]);
            setHasMoreItems(false);
          } else {
            toastError("Course not found");
            setCourses([]);
            setHasMoreItems(false);
          }
        })
        .catch(() => {
          toastError("Course not found");
          setCourses([]);
          setHasMoreItems(false);
        })
        .finally(() => setIsLoadingMore(false));
    }

    // 2) search by 12-char id_course (string) → try details first, then fall back to list
    if (isIdCode12) {
      dispatch(clearCourseListData());
      setCourses([]);
      return dispatch(
        course_details({
          body: {
            id_adminuser,
            id_course: trimmed, // string code path
            stateFormat: 4,
            countryFormat: 4,
            detailLevel: 2,
          },
          user: userPayload,
        })
      )
        .then((resp) => {
          if (resp?.payload?.data?.Status === 1) {
            setCourses([resp.payload.data]);
            setHasMoreItems(false);
            return;
          }
          // not found → toast + fall back to list search by name
          toastError("Course not found");
          return dispatch(
            course_list({
              body: {
                id_adminuser,
                courseName: trimmed,
                stateFormat: 4,
                page,
                resultsPerPage: 100,
              },
              user: userPayload,
            })
          ).then((r2) => {
            const list = r2?.payload?.data?.courseList || [];
            const total = r2?.payload?.data?.totalCourses || 0;
            setCourses(list);
            const maxPageReached = page >= 5;
            setHasMoreItems(!maxPageReached && list.length < total);
          });
        })
        .catch(() => {
          toastError("Course not found");
        })
        .finally(() => setIsLoadingMore(false));
    }

    // 3) normal name search
    return dispatch(
      course_list({
        body: {
          id_adminuser,
          courseName: trimmed,
          stateFormat: 4,
          page,
          resultsPerPage: 100,
        },
        user: userPayload,
      })
    )
      .then((resp) => {
        const fetched = resp?.payload?.data?.courseList || [];
        const total = resp?.payload?.data?.totalCourses || 0;
        setCourses((prev) => {
          const next = reset ? fetched : [...prev, ...fetched];
          const maxPageReached = page >= 5;
          setHasMoreItems(!maxPageReached && next.length < total);
          return next;
        });
      })
      .finally(() => setIsLoadingMore(false));
  },
  [
    courseData.searchedCourseName,
    secret_Key,
    profileDetails?.username,
    id_adminuser,
    dispatch,
  ]
	);


	const redirectToMap = async (course) => {
  sessionStorage.removeItem("prevSetHole");

  // ensure we have id_courseInt; if only id_course (string) is present, fetch details to get it
  const ensureIdCourseInt = async () => {
    if (course?.id_courseInt) return course.id_courseInt;

    if (course?.id_course) {
      const resp = await dispatch(
        course_details({
          body: {
            id_adminuser,
            id_course: course.id_course, // string code path
            stateFormat: 4,
            countryFormat: 4,
            detailLevel: 2,
          },
          user: {
            secretKey: secret_Key,
            userName: profileDetails?.username,
          },
        })
      );
      const ok = resp?.payload?.data?.Status === 1;
      const idInt = resp?.payload?.data?.id_courseInt;
      if (ok && idInt) return idInt;
      toastError("Course not found");
      return null;
    }

    toastError("Course not found");
    return null;
  };

  const id_courseInt = await ensureIdCourseInt();
  if (!id_courseInt) return;

  if (course?.gpsAvailable && course?.vectorAvailable) {
    navigate(`/mapview/coursemap?course=${id_courseInt}`);
    return;
  }

  // fallback: geocode address, then open map if geocode succeeds
  let addr = "";
  if (course?.courseName) addr += course.courseName;
  if (course?.address1) addr += `,${course.address1}`;
  if (course?.address2) addr += `,${course.address2}`;
  if (course?.city) addr += `,${course.city}`;
  if (course?.stateFull) addr += `,${course.stateFull}`;
  if (course?.zipCode) addr += `,${course.zipCode}`;

  try {
    const res = await dispatch(fetch_google_geocode({ address: addr }));
    if (res?.payload?.results?.length) {
      navigate(`/mapview/coursemap?course=${id_courseInt}`);
    } else {
      toastError("No course found!");
    }
  } catch {
    toastError("No course found!");
  }
	};



	const toEditCourseInfoPage = (e, courseIdInt) => {
		navigate(`/editCourseInformation/course?courseId=${courseIdInt}`);
	};

	const redirectToScorecard = (courseId) => {
		navigate(`/scoreDetails/course?courseId=${courseId}`);
	};

	const sortedCourseList = React.useMemo(() => {
		if (courses.length) {
			let sortableItems = [...courses];
			if (sortConfig.key) {
				sortableItems.sort((a, b) => {
					if (a[sortConfig.key] < b[sortConfig.key]) {
						return sortConfig.direction === "asc" ? -1 : 1;
					}
					if (a[sortConfig.key] > b[sortConfig.key]) {
						return sortConfig.direction === "asc" ? 1 : -1;
					}
					return 0;
				});
			}
			return sortableItems;
		}
		return [];
	}, [courses, sortConfig]);

	const requestSort = (key) => {
		let direction = "asc";
		if (sortConfig.key === key && sortConfig.direction === "asc") {
			direction = "desc";
		}
		setSortConfig({ key, direction });
	};

	if (location.pathname === "/coursemap") {
		sessionStorage.removeItem("prevSetHole");
	}

	const handleScroll = useCallback(() => {
		if (!containerRef.current) return;
		const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
		if (
			scrollHeight - scrollTop - clientHeight < 100 &&
			hasMoreItems &&
			!isLoadingMore && 
			page < 5
		) {
			const next = page + 1;
			setPage(next);	
			fetchCourseList(next);
		}
	}, [page, hasMoreItems, isLoadingMore, fetchCourseList]);

	// now we can safely debounce that callback
	const debouncedHandleScroll = useMemo(
		() => debounce(handleScroll, 200),
		[handleScroll]
	);

	useEffect(() => {
		return () => {
			debouncedHandleScroll.cancel();
		};
	}, [debouncedHandleScroll]);

	return (
		<div className="main_wraper">
			<Wrapper isHeader={true} headerTitle={"COURSE MAP"}>
				<div className={styles.range_sec}>
					<Table
						title="Courses"
						addIcon={true}
						addIconOnClick={() => navigate("/courseinformation")}
					>
						{(courseData?.course_list_status === "loading" &&
							courses.length === 0) ||
						(courseData?.course_details_status === "loading" &&
							courses.length === 0) ? (
							<HeatMapListSkeleton />
						) : (
							<>
								{courses.length > 0 ? (
									<>
										{courses.length !== 0 ? (
											<div
												className={styles.new_table}
												ref={containerRef}
												onScroll={debouncedHandleScroll}
											>
												<table className={styles.table_bar}>
													<thead>
														<tr style={{ cursor: "pointer" }}>
															<th
																className={styles.id}
																onClick={() => requestSort("id_courseInt")}
															>
																ID
															</th>
															<th
																className={styles.name}
																onClick={() => requestSort("courseName")}
															>
																NAME
															</th>
															<th
																className={styles.city}
																onClick={() => requestSort("city")}
															>
																CITY
															</th>
															<th
																className={styles.state}
																onClick={() => requestSort("stateShort")}
															>
																STATE
															</th>
															<th
																className={styles.country}
																onClick={() => requestSort("countryFull")}
															>
																COUNTRY
															</th>
															<th
																className={styles.state}
																onClick={() => requestSort("id_course")}
															>
																ID COURSE
															</th>
															<th
																className={styles.state}
																onClick={() => requestSort("layoutHoles")}
															>
																LAYOUT
															</th>
															<th
																className={styles.one_meter}
																onClick={() => requestSort("1meterElevationAvailable")}
															>
																ONE METER
															</th>
															<th
																className={styles.state}
																onClick={() => requestSort("greenHeatmapAvailable")}
															>
																HEATMAPS
															</th>
															<th
																className={styles.modyfied}
																onClick={() => requestSort("courseModified")}
															>
																LAST MODIFIED
															</th>
															<th className={styles.action}>ACTION</th>
														</tr>
													</thead>
													<tbody>
														{sortedCourseList.map((course) => (
															<CourseMapListTableRowItem
																key={course.id_course}
																course={course}
																onMapClick={redirectToMap}
																onScorecardClick={redirectToScorecard}
																onEditClick={toEditCourseInfoPage}
															/>
														))}
														{hasMoreItems && (
															<TableSkeletonRows rows={3} columns={11} />
														)}
													</tbody>
												</table>
											</div>
										) : (
											<div className={styles.searchDrivingRange}>
												<SearchOffIcon className={styles.notDataicon} />
												<p className={styles.notDataContent}>
													No Courses Found
												</p>
												<p className={styles.dataSearchContent}>
													Please refine your search request
												</p>
											</div>
										)}
									</>
								) : (
									<div className={styles.searchDrivingRange}>
										<SearchIcon className={styles.notDataicon} />

										<p className={styles.notDataContent}>
											Please Search For Courses
										</p>
									</div>
								)}
							</>
						)}
					</Table>
				</div>
			</Wrapper>
		</div>
	);
};

export default CourseMapList;
